import { Component, inject, OnInit, OnDestroy, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, switchMap, of, filter, take } from 'rxjs';

import { BaaService } from '../../service/entity-services/baa.service';
import { UsersService } from '../../service/entity-services/users.service';
import { Baa } from '../../objects/baa';
import { EntityType } from '../../objects/interface-library';
import { ViewEntity } from '../../objects/view-entity';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { VendorConnectionsComponent } from '../../entity-form-components/vendor-connections/vendor-connections.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';
import { NotesComponent } from '../../entity-form-components/notes/notes.component';
import { VersionsComponent } from '../../entity-form-components/versions/versions.component';
import { AssessmentConnectionsComponent } from '../../entity-form-components/assessment-connections/assessment-connections.component';
import { WorkflowConnectionsComponent } from '../../entity-form-components/workflow-connections/workflow-connections.component';
import { ContractConnectionsComponent } from '../../entity-form-components/contract-connections/contract-connections.component';
import { PeopleTableComponent } from '../../entity-form-components/people-table/people-table.component';
import { NoteRecord } from '../../objects/note-record';

@Component({
  selector: 'app-baa-record',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatSidenavModule,
    MatToolbarModule,
    DocumentViewerSidenavComponent,
    EntityActionsSidenavComponent,
    VendorConnectionsComponent,
    AttachmentConnectionsComponent,
    CriticalDateConnectionsComponent,
    NotesComponent,
    VersionsComponent,
    AssessmentConnectionsComponent,
    WorkflowConnectionsComponent,
    ContractConnectionsComponent,
    PeopleTableComponent
  ],
  templateUrl: './baa-record.component.html',
  styleUrls: ['./baa-record.component.css']
})
export class BaaRecordComponent extends ViewEntity implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public baaService = inject(BaaService);
  public usersService = inject(UsersService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  currentBaa: Baa | null = null;
  loading = true;
  error: string | null = null;

  // Display-friendly values
  partyName: string = '';
  businessAssociateName: string = '';
  coveredEntityName: string = '';
  legalEntitiesDisplay: string = '';
  sitesDisplay: string = '';
  primaryOwnerName: string = '';
  secondaryOwnerName: string = '';
  tertiaryOwnerName: string = '';
  baCoordinatorName: string = '';
  ceCoordinatorName: string = '';
  baSignatoryName: string = '';
  ceSignatoryName: string = '';
  coveredEntityNotificationName: string = '';
  businessAssociateNotificationName: string = '';

  // Entity IDs for clickable links
  partyVendorNum: string = '';
  businessAssociateVendorNum: string = '';
  coveredEntityVendorNum: string = '';
  primaryOwnerId: string = '';
  secondaryOwnerId: string = '';
  tertiaryOwnerId: string = '';
  baCoordinatorId: string = '';
  ceCoordinatorId: string = '';
  baSignatoryId: string = '';
  ceSignatoryId: string = '';
  businessAssociateNotificationId: string = '';
  coveredEntityNotificationId: string = '';

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editBaa(),
      disabled: false
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachment(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addCriticalDate(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Associated Contracts', index: 0, icon: 'link' },
    { label: 'Attachments', index: 1, icon: 'attach_file' },
    { label: 'Dates', index: 2, icon: 'event' },
    { label: 'People', index: 3, icon: 'person' },
    { label: 'BAA History', index: 4, icon: 'history' },
    { label: 'Provisions', index: 5, icon: 'gavel' },
    { label: 'Notes', index: 6, icon: 'note' },
    { label: 'Assessments', index: 7, icon: 'assessment' },
  ]);

  ngOnInit() {
    const baaId = this.route.snapshot.paramMap.get('id');

    if (!baaId) {
      this.error = 'No BAA ID provided';
      this.loading = false;
      return;
    }

    this.loadBaaFromCache(baaId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBaaFromCache(baaNum: string) {
    // console.log('[loadBaaFromCache] Starting to load BAA:', baaNum);
    this.loading = true;
    this.error = null;

    // Wait for BAAs to be loaded (array has data), then find and hydrate
    this.baaService.visibleBaas$
      .pipe(
        takeUntil(this.destroy$),
        filter(baas => {
          // console.log('[loadBaaFromCache] Received BAAs array, length:', baas.length);
          return baas.length > 0;
        }), // Only proceed when BAAs are loaded
        take(1), // Only take the first emission after filter passes
        switchMap(baas => {
          const baa = baas.find(b => b.baaNum === baaNum);

          if (!baa) {
            console.error('[loadBaaFromCache] BAA not found in array');
            // BAA not found
            throw new Error('BAA not found');
          }

          // console.log('[loadBaaFromCache] Found BAA:', baa);

          // Now get the fully hydrated BAA
          if (baa.baaId) {
            return this.baaService.getBaaObjectFullyHydrated(baa.baaId);
          } else {
            // If no baaId, return the BAA as-is (shouldn't happen)
            return of(baa);
          }
        })
      )
      .subscribe({
        next: (hydratedBaa) => {
          console.log('[BAA:Diag] Received hydrated BAA:', hydratedBaa?.baaId || 'null');
          this.currentBaa = hydratedBaa;
          this.processBaaData();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[BAA:Diag] Error loading BAA:', err);
          this.error = err.message || 'Failed to load BAA. Please try again.';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  processBaaData() {
    if (!this.currentBaa) return;

    // Extract display-friendly names
    this.partyName = this.extractDisplayName(
      this.currentBaa.party,
      'name'
    );

    this.businessAssociateName = this.extractDisplayName(
      this.currentBaa.businessAssociateName,
      'name',
      'businessAssociateName'
    );

    this.coveredEntityName = this.extractDisplayName(
      this.currentBaa.coveredEntity,
      'name',
      'coveredEntity'
    );

    // Handle legal entities (can be single object or array)
    if (this.currentBaa.legalEntities) {
      if (Array.isArray(this.currentBaa.legalEntities) && this.currentBaa.legalEntities.length > 0) {
        // Handle array of legal entities
        this.legalEntitiesDisplay = this.currentBaa.legalEntities
          .map(le => this.extractDisplayName(le, 'legalEntityName'))
          .join(', ');
      } else if (typeof this.currentBaa.legalEntities === 'object') {
        // Handle single legal entity object
        this.legalEntitiesDisplay = this.extractDisplayName(this.currentBaa.legalEntities, 'legalEntityName');
      }
    }

    // Handle sites array
    if (Array.isArray(this.currentBaa.sites) &&
        this.currentBaa.sites.length > 0) {
      this.sitesDisplay = this.currentBaa.sites
        .map(site => this.extractDisplayName(site, 'name'))
        .join(', ');
    }

    // Handle users/people - use extractOwnerDisplayName for hydrated user objects
    this.primaryOwnerName = this.extractOwnerDisplayName(
      this.currentBaa.primaryOwner
    );

    this.secondaryOwnerName = this.extractOwnerDisplayName(
      this.currentBaa.secondaryOwner
    );

    this.tertiaryOwnerName = this.extractOwnerDisplayName(
      this.currentBaa.tertiaryOwner
    );

    this.baCoordinatorName = this.extractOwnerDisplayName(
      this.currentBaa.baCoordinator
    );

    this.ceCoordinatorName = this.extractOwnerDisplayName(
      this.currentBaa.ceCoordinator
    );

    this.baSignatoryName = this.extractOwnerDisplayName(
      this.currentBaa.baSignatory
    );

    this.ceSignatoryName = this.extractOwnerDisplayName(
      this.currentBaa.ceSignatory
    );

    // Handle notification fields (may be objects or strings)
    // Try friendlyName first, then fullName, then email
    this.coveredEntityNotificationName = this.extractNotificationName(
      this.currentBaa.coveredEntityNotification
    );

    this.businessAssociateNotificationName = this.extractNotificationName(
      this.currentBaa.businessAssociateNotification
    );

    // Extract entity IDs for clickable links
    this.partyVendorNum = this.extractVendorNum(this.currentBaa.party);
    this.businessAssociateVendorNum = this.extractVendorNum(this.currentBaa.businessAssociateName);
    this.coveredEntityVendorNum = this.extractVendorNum(this.currentBaa.coveredEntity);
    this.primaryOwnerId = this.extractUserId(this.currentBaa.primaryOwner);
    this.secondaryOwnerId = this.extractUserId(this.currentBaa.secondaryOwner);
    this.tertiaryOwnerId = this.extractUserId(this.currentBaa.tertiaryOwner);
    this.baCoordinatorId = this.extractPersonId(this.currentBaa.baCoordinator);
    this.ceCoordinatorId = this.extractPersonId(this.currentBaa.ceCoordinator);
    console.log('[BAA:Diag] ceCoordinator raw value:', this.currentBaa.ceCoordinator, '→ extracted ID:', this.ceCoordinatorId);
    console.log('[BAA:Diag] baCoordinator raw value:', this.currentBaa.baCoordinator, '→ extracted ID:', this.baCoordinatorId);
    this.baSignatoryId = this.extractUserId(this.currentBaa.baSignatory);
    this.ceSignatoryId = this.extractUserId(this.currentBaa.ceSignatory);
    this.businessAssociateNotificationId = this.extractPersonId(this.currentBaa.businessAssociateNotification);
    this.coveredEntityNotificationId = this.extractPersonId(this.currentBaa.coveredEntityNotification);

    // Load document using base class method
    if (this.currentBaa.linkToBaa) {
      this.loadDocument(this.currentBaa.linkToBaa);
    }

    // Load critical dates associated with this BAA by entityId
    if (this.currentBaa.baaId) {
      this.criticalDatesService.getDatesByEntityId(this.currentBaa.baaId)
        .subscribe(dates => {
          this.entityDates = dates;
        });
    } else {
      this.entityDates = [];
    }

    // Load attachments associated with this BAA
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    if (this.currentBaa.baaId) {
      this.attachmentsService.getAttachmentsForEntity(this.currentBaa.baaId, 'Baa')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentBaa?.attachments?.length) {
            // Fallback: ETL'd BAAs may not have parentEntityId on attachments
            this.attachmentsService.getAttachmentsByNumbers(this.currentBaa.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
                this.cdr.detectChanges();
              });
          } else {
            this.entityAttachments = [];
          }
          this.cdr.detectChanges();
        });
    } else {
      this.entityAttachments = [];
    }
  }

  private extractVendorNum(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.vendorNum || '';
  }

  private extractUserId(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.email || value.userId || value.emailAddress || '';
  }

  private extractPersonId(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.contactNum || value.email || value.userId || '';
  }

  openEntityLink(route: string, id: string): void {
    if (id) {
      window.open(`/securehome/${route}/${id}`, '_blank');
    }
  }

  private extractDisplayName(
    value: any,
    primaryKey: string,
    fallbackKey?: string
  ): string {
    if (!value) return 'N/A';

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      // Check for nested structure (e.g., legalEntityData, siteData)
      // For legal entities: {legalEntityData: {...}, legalEntityNum: "..."}
      if (value.legalEntityData && value.legalEntityData.legalEntityName) {
        return value.legalEntityData.legalEntityName;
      }

      // For sites: {siteData: {...}, siteNum: "..."}
      if (value.siteData && value.siteData.siteName) {
        return value.siteData.siteName;
      }

      // Direct property access (flat structure)
      if (value[primaryKey]) return value[primaryKey];

      // Common fallback for sites (siteName is the actual property name)
      if (primaryKey === 'name' && value.siteName) return value.siteName;

      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
    }

    return 'N/A';
  }

  /**
   * Extract display name from notification field (person object)
   * Tries friendlyName first, then fullName, then email
   */
  private extractNotificationName(notification: any): string {
    if (!notification) return 'N/A';

    if (typeof notification === 'string') {
      return notification;
    }

    if (typeof notification === 'object') {
      // Try friendlyName first (if not empty)
      if (notification.friendlyName && notification.friendlyName.trim()) {
        return notification.friendlyName;
      }
      // Then try fullName
      if (notification.fullName && notification.fullName.trim()) {
        return notification.fullName;
      }
      // Then try to construct from firstName/lastName
      if (notification.firstName || notification.lastName) {
        const parts = [notification.firstName, notification.lastName].filter(Boolean);
        if (parts.length > 0) return parts.join(' ');
      }
      // Finally fall back to email
      if (notification.email) {
        return notification.email;
      }
    }

    return 'N/A';
  }

  /**
   * Extract display name from owner field (could be hydrated object or email string)
   * If backend returns hydrated object: { userId: "email", userData: { email, given_name, family_name, ... } }
   * If backend returns person object: { firstName: "...", lastName: "...", fullName: "..." }
   * If backend returns email string: Return the email as-is
   * Display format: "Last Name, First Name"
   */
  private extractOwnerDisplayName(owner: any): string {
    if (!owner) return 'Not Assigned';

    // Handle hydrated user object (new format from backend)
    if (typeof owner === 'object' && owner.userData) {
      const { given_name, family_name, email } = owner.userData;
      if (given_name && family_name) {
        return `${family_name}, ${given_name}`;
      }
      return email || owner.userId || 'Unknown User';
    }

    // Handle person object with firstName and lastName
    if (typeof owner === 'object' && owner.firstName && owner.lastName) {
      return `${owner.lastName}, ${owner.firstName}`;
    }

    // Handle person object with fullName only
    if (typeof owner === 'object' && owner.fullName) {
      return owner.fullName;
    }

    // Handle legacy string format (email)
    if (typeof owner === 'string') {
      return owner;
    }

    // Handle old object format with friendlyName (should not happen with new backend)
    if (typeof owner === 'object' && owner.friendlyName) {
      return owner.friendlyName;
    }

    if (typeof owner === 'object' && owner.email) {
      return owner.email;
    }

    return 'Unknown User';
  }

  editBaa() {
    if (this.currentBaa?.baaNum) {
      this.router.navigate(['/securehome/new-baa', this.currentBaa.baaNum]);
    }
  }

  goBack() {
    this.router.navigate(['/securehome/baas']);
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  // Action methods
  addAttachment(): void {
    const entityObjectForDialog = {
      ...this.currentBaa,
      entityType: EntityType.Baa
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentBaa) return;

      if (newAttachment && newAttachment.attachmentNum) {
        // NEW API: Attachment was created with associatedEntityId
        // Just refresh from the entity-based endpoint
        if (this.currentBaa.baaId) {
          this.attachmentsService.getAttachmentsForEntity(this.currentBaa.baaId, 'Baa')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                this.entityAttachments = [...this.entityAttachments, newAttachment];
              }
            });
        }
      }
    });
  }

  addCriticalDate(): void {
    // Use the base class method from ViewEntity (call via super to avoid recursion)
    super['addCriticalDate'](
      this.currentBaa,
      'baaNum',
      (id: string, update: any) => {
        // Extract the attribute name and value from the update object
        const attribute = Object.keys(update)[0];
        const value = update[attribute];
        return this.baaService.updateBaaAttribute(id, attribute, value);
      }
    );
  }

  refreshAttachments(): void {
    if (this.currentBaa?.baaId) {
      this.attachmentsService.getAttachmentsForEntity(this.currentBaa.baaId, 'Baa')
        .pipe(takeUntil(this.destroy$))
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentBaa?.attachments?.length) {
            this.attachmentsService.getAttachmentsByNumbers(this.currentBaa.attachments)
              .pipe(takeUntil(this.destroy$))
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    }
  }

  refreshCriticalDates(): void {
    if (this.currentBaa?.baaId) {
      this.criticalDatesService.getDatesByEntityId(this.currentBaa.baaId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(dates => {
          this.entityDates = dates;
        });
    }
  }

  saveNotes(notes: NoteRecord[]): void {
    if (!this.currentBaa) return;
    this.currentBaa.notes = notes;
    this.baaService.writeBaa(this.currentBaa)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to save notes:', err)
      });
  }
}
